import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

/**
 * /learn/saint-vs-ampa-one — PROMPT 5
 * We are the only Florida clinic offering both SAINT® and Ampa One, so no
 * competitor can write this page. SAINT® = fMRI-personalized, 5-day,
 * randomized-trial data. Ampa One = camera-guided, minutes-long, accelerated
 * and one-day schedules at a lower cost.
 */

const AMPA_TRADEMARK =
  "Ampa and Ampa One are trademarks of Ampa Inc. Neuro Wellness TMS Centers of America is an independent provider and is not owned by Ampa.";

const faqs: LearnFAQ[] = [
  {
    question: "Is Ampa One the same as SAINT?",
    answer:
      "No. SAINT® uses an fMRI brain scan to personalize your stimulation target and runs over 5 days (FDA 510(k) K220177). Ampa One uses camera-guided targeting with a pre-printed cap and a camera built into the coil, takes minutes per session, and supports accelerated and one-day schedules. Both are FDA-cleared TMS for depression that hasn't improved on medication, but they are different systems with different targeting methods.",
  },
  {
    question: "Which is more effective, SAINT® or Ampa One?",
    answer:
      "They have not been compared head-to-head. SAINT® produced 78.6% remission in a 2022 randomized controlled trial (Cole et al., Am J Psychiatry 2022) — a striking result from a small single-site study. Ampa One has reported early, non-randomized results (87% response, 72% remission at 6 weeks, per a November 2025 NRx Pharmaceuticals press release). Neither has been compared to the other directly. Individual results vary.",
  },
  {
    question: "Which is cheaper, SAINT® or Ampa One?",
    answer:
      "Ampa One is generally the lower-cost option. SAINT® starts at $25,000 for the full 5-day course (self-pay $25,000–$28,000) and has a Medicare hospital outpatient rate of $19,703. A standard Ampa One course is commonly covered by insurance, and accelerated/one-day schedules are typically self-pay at a lower price point. We quote your exact Ampa One self-pay price at your free consultation and verify your benefits either way.",
  },
  {
    question: "Can I do Ampa One if I can't get an MRI?",
    answer:
      "Yes — that is one of Ampa One's advantages. SAINT® requires an fMRI brain scan to personalize the target, which not everyone can have (certain implants, claustrophobia, availability). Ampa One uses camera-guided targeting with a pre-printed cap and does not require an MRI. If you cannot get an MRI, Ampa One may be the better fit. Our physicians help you decide at a free consultation.",
  },
];

export const pageSaintVsAmpaOne = makeArticle({
  slug: "saint-vs-ampa-one",
  title: "SAINT® vs Ampa One™: Which Accelerated TMS Is Right for You?",
  description:
    "SAINT® uses fMRI targeting over 5 days with randomized-trial data; Ampa One uses camera-guided targeting with minutes-long sessions and accelerated schedules at a lower cost. We offer both in Florida.",
  canonical: "/learn/saint-vs-ampa-one",
  h1: "SAINT® vs Ampa One: Two Accelerated TMS Options, One Florida Clinic",
  breadcrumbLabel: "SAINT® vs Ampa One",
  shortAnswer:
    "Both are FDA-cleared TMS for depression that hasn't improved on medication, and both can shorten treatment far below the usual 6 weeks. SAINT® uses an fMRI brain scan to personalize your target and runs over 5 days, with randomized-trial data behind it. Ampa One uses camera-guided targeting with a pre-printed cap, takes minutes per session, and supports accelerated and one-day schedules at a lower cost. Our physicians help you choose at a free consultation. Individual results vary. No outcome is guaranteed. Coverage varies by plan. Ampa One is available by prescription after a clinical evaluation.",
  keyTakeaways: [
    "Both are FDA-cleared TMS for depression that hasn't improved on medication.",
    "SAINT®: fMRI-personalized targeting, 5 days, randomized-trial data (78.6% remission, Cole et al. 2022).",
    "Ampa One: camera-guided cap targeting, minutes-long sessions, accelerated and one-day schedules, lower cost.",
    "SAINT® requires an fMRI scan; Ampa One does not require an MRI.",
    "We are the only Florida clinic offering both — our physicians help you choose at a free consultation.",
  ],
  sections: [
    {
      heading: "Quick answer",
      paragraphs: [
        "SAINT® and Ampa One are both FDA-cleared TMS for depression that hasn't improved on medication, and both can shorten treatment far below the usual 6 weeks. They differ in targeting, schedule, evidence, and price. Because we offer both, our physicians help you choose based on your situation — not on which device we happen to sell.",
      ],
    },
    {
      heading: "Side by side",
      paragraphs: [],
      table: {
        headers: ["", "SAINT®", "Ampa One"],
        rows: [
          [
            "Targeting",
            "fMRI-personalized (brain scan chooses your target)",
            "Camera-guided (pre-printed cap + camera in the coil)",
          ],
          [
            "Days",
            "5 days",
            "Standard, accelerated (as few as 1–2 days), or one-day",
          ],
          [
            "Sessions",
            "10 sessions/day for 5 days (~50 total)",
            "Minutes-long sessions; schedule varies",
          ],
          [
            "Evidence",
            "Randomized trials: Cole et al., Am J Psychiatry 2020 and 2022 (78.6% remission)",
            "Early non-randomized results (87% response, 72% remission at 6 weeks, NRx press release Nov 2025)",
          ],
          [
            "FDA clearance",
            "SAINT® K220177 (September 2022)",
            "Ampa One cleared for MDD",
          ],
          [
            "Price",
            "From $25,000 (self-pay $25,000–$28,000); Medicare rate $19,703",
            "Lower self-pay; standard course commonly covered by insurance",
          ],
          [
            "Insurance",
            "Medicare rate; commercial plans may approve via prior auth",
            "Standard course commonly covered; accelerated/one-day typically self-pay",
          ],
          ["Requires MRI", "Yes (fMRI)", "No"],
        ],
      },
    },
    {
      heading: "Who each fits best",
      paragraphs: [
        "SAINT® fits patients who want the protocol with randomized-trial data and fMRI-personalized targeting, and who can get an MRI and travel for a 5-day week. Ampa One fits patients whose biggest barrier is time or who cannot get an MRI, and who want shorter sessions and a lower cost. Both require that depression hasn't improved enough on antidepressants.",
      ],
    },
    {
      heading: "Can I start with one and switch?",
      paragraphs: [
        "It is possible to start with one and, if you don't get the response you hoped for, discuss the other with your physician — but they are different treatments, not interchangeable doses, and a switch is a clinical decision. Our physicians track your response (PHQ-9 before, during, and after) and help you decide. We discuss any switch as part of your treatment plan at your consultation.",
      ],
    },
    {
      heading: "Why we offer both",
      paragraphs: [
        "We are the only Florida clinic offering both SAINT® and Ampa One. That means we can recommend the one that fits you, rather than the one we happen to have. Our physicians help you choose at a free consultation. See also [[SAINT® vs accelerated TMS|/learn/saint-vs-accelerated-tms]] and [[SAINT® vs one-day TMS|/learn/saint-vs-one-day-tms]].",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, Stimpson KH, Bentzley BS, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "U.S. FDA. 510(k) K220177 — Magnus Neuromodulation System with SAINT Technology (September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "Ampa Inc. Ampa One — FDA-cleared TMS system for major depressive disorder.",
      url: "https://www.ampahealth.com/",
    },
  ],
  relatedLinks: [
    {
      title: "Ampa One™ TMS in Florida",
      href: "/ampa",
      description: "Camera-guided, accelerated depression treatment",
    },
    {
      title: "SAINT® Accelerated TMS",
      href: "/saint",
      description: "The 5-day, fMRI-guided protocol",
    },
    {
      title: "Ampa One vs NeuroStar vs BrainsWay",
      href: "/learn/ampa-one-vs-neurostar-vs-brainsway",
      description: "Compare TMS devices by coil, targeting, and clearance",
    },
    {
      title: "Ampa One TMS Cost",
      href: "/learn/ampa-tms-cost",
      description: "Price, insurance, and payment options",
    },
    {
      title: "Schedule a Consultation",
      href: "/contactus",
      description: "Book a free consultation with our clinical team",
    },
  ] as RL[],
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    { label: "Quick answer", href: "#quick-answer" },
    { label: "Side by side", href: "#side-by-side" },
    { label: "Who each fits", href: "#who-each-fits-best" },
    { label: "Switching", href: "#can-i-start-with-one-and-switch" },
    { label: "Why we offer both", href: "#why-we-offer-both" },
    { label: "FAQ", href: "#faq" },
    { label: "Cited research", href: "#cited-research" },
  ],
  safetyBlock:
    "Individual results vary. No outcome is guaranteed. Coverage varies by plan. Ampa One is available by prescription after a clinical evaluation. SAINT® is contraindicated for people with non-removable magnetic-sensitive metal in or near the head. If you are in crisis, call or text 988.",
  trademarkFooter: AMPA_TRADEMARK,
  ctaBand: {
    headline: "See which accelerated TMS fits you, free",
    primaryText: "Request a free consultation",
    primaryHref: "/ampa",
    secondaryText: "Call 954-827-9793",
    secondaryHref: "tel:9548279793",
    phone: "954-827-9793",
  },
});
